// @ts-check
// Seção "Fluxos de automação" na página do card (spec 2.0.0, fluxos.md seção 20.15, item 2; contrato 09 de fluxos,
// seção 17): os fluxos em que o card está (situação, passo atual e quando retoma) e os cinco últimos encerrados (com o
// motivo). Quem tem WORKFLOWS ainda vê "Remover do fluxo" e "Inserir em um fluxo"; os outros só os nomes e as situações.
// Some quando o card nunca entrou num fluxo e a pessoa não pode inserir.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Link } from '../../app/router.js';
import { listFlowsForRecord, removeLeadsFromFlow } from '../../data/flows.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { OPEN_FLOW_RUN_STATUSES } from '../../../../shared/flows/flow-reasons.js';
import { flowEditorHref } from '../../lib/flow-list.js';
import { openFlowInsertLeadsDialog } from './flow-insert-leads-dialog.js';
import { flowReasonText, waitResumeText } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/flows.js').RecordFlowRun} RecordFlowRun */

const CARD_OBJECT_NAME = 'enrollmentOpportunity';
const CLOSED_SHOWN = 5;

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: Record<string, any>, onChanged?: () => void }} props
 */
export function RecordFlowsSection({ objectMetadata, record, onChanged }) {
  const isCard = objectMetadata?.nameSingular === CARD_OBJECT_NAME;
  const [state, setState] = useState(/** @type {{ runs: RecordFlowRun[], canManage: boolean } | null} */ (null));
  const [busyRunId, setBusyRunId] = useState(/** @type {string | null} */ (null));
  const load = () => {
    if (!isCard || !record?.id) return;
    listFlowsForRecord(record.id).then(setState).catch(() => setState({ runs: [], canManage: false }));
  };
  useEffect(load, [record?.id, isCard]);
  if (!isCard || !state) return null;
  const open = state.runs.filter((run) => OPEN_FLOW_RUN_STATUSES.includes(run.status));
  const closed = state.runs.filter((run) => !OPEN_FLOW_RUN_STATUSES.includes(run.status)).slice(0, CLOSED_SHOWN);
  if (open.length === 0 && closed.length === 0 && !state.canManage) return null;

  /** @param {RecordFlowRun} run */
  const remove = async (run) => {
    setBusyRunId(run.id);
    try {
      await removeLeadsFromFlow(run.flowId, [record.id]);
      showSuccessSnackbar(t('flows.recordSection.removed', { name: run.flowName }));
      load();
      onChanged?.();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyRunId(null);
    }
  };
  /** @param {RecordFlowRun} run */
  const nameOf = (run) => (state.canManage
    ? html`<${Link} href=${flowEditorHref(run.flowId)} className="flow-record-name">${run.flowName}<//>`
    : html`<span class="flow-record-name">${run.flowName}</span>`);

  return html`<section class="flow-record-section" data-testid="record-flows-section" aria-labelledby="flow-record-title">
    <div class="flow-record-header">
      <h2 class="flow-record-title" id="flow-record-title"><${Icon} name="IconSitemap" size=${14} />${t('flows.recordSection.title')}</h2>
      ${state.canManage
        ? html`<${Button} title=${t('flows.recordSection.insert')} icon="IconPlus" variant="secondary" size="small"
            onClick=${() => openFlowInsertLeadsDialog({ objectMetadata, recordIds: [record.id], onDone: load })} />`
        : null}
    </div>
    ${open.length === 0 && closed.length === 0 ? html`<p class="flow-field-hint">${t('flows.recordSection.empty')}</p>` : null}
    ${open.length > 0
      ? html`<ul class="flow-record-list">
          ${open.map((run) => html`<li key=${run.id} class="flow-record-run">
            <div class="flow-record-run-main">
              ${nameOf(run)}
              <span class="flow-run-status" data-status=${run.status}>${t(`flows.runs.statuses.${run.status}`)}</span>
            </div>
            <div class="flow-record-run-detail">
              ${run.currentNode ? html`<span>${run.currentNode.title ?? t(`flows.nodes.${run.currentNode.type}.name`)}</span>` : null}
              ${run.resumeAt ? html`<span>${waitResumeText(run)}</span>` : null}
            </div>
            ${state.canManage
              ? html`<${Button} title=${t('flows.recordSection.remove')} variant="tertiary" accent="danger" size="small" isLoading=${busyRunId === run.id}
                  onClick=${() => remove(run)} ariaLabel=${t('flows.recordSection.removeFrom', { name: run.flowName })} />`
              : null}
          </li>`)}
        </ul>`
      : null}
    ${closed.length > 0
      ? html`<h3 class="flow-record-subtitle">${t('flows.recordSection.closed')}</h3>
        <ul class="flow-record-list" data-closed="true">
          ${closed.map((run) => html`<li key=${run.id} class="flow-record-run">
            <div class="flow-record-run-main">
              ${nameOf(run)}
              <span class="flow-run-status" data-status=${run.status}>${t(`flows.runs.statuses.${run.status}`)}</span>
            </div>
            <div class="flow-record-run-detail">
              ${run.status === 'COMPLETED'
                ? html`<span>${run.endedAt ? t('flows.runs.completedAt', { date: formatDateTime(run.endedAt) }) : t('flows.runs.exitReasons.END')}</span>`
                : html`<span>${flowReasonText(run.status === 'STOPPED' ? 'STOP' : 'EXIT', run.exitReason)}</span>
                  ${run.endedAt ? html`<span>${formatDateTime(run.endedAt)}</span>` : null}`}
            </div>
          </li>`)}
        </ul>`
      : null}
  </section>`;
}
